import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import type { ResolvedRun } from '@/hooks/useLevelRun'

export interface EndlessRunState {
  active: boolean
  streak: number
  score: number
  coins: number
  finished: boolean
}

const IDLE_ENDLESS: EndlessRunState = { active: false, streak: 0, score: 0, coins: 0, finished: false }

interface GameContextValue {
  lastRun: ResolvedRun | null
  lastLevelIndex: number | null
  setLastRun: (levelIndex: number, run: ResolvedRun) => void
  endlessRun: EndlessRunState
  beginEndlessRun: () => void
  recordEndlessLevel: (score: number, coins: number) => void
  endEndlessRun: () => void
  clearEndlessRun: () => void
}

const GameContext = createContext<GameContextValue | null>(null)

export function GameProvider({ children }: { children: ReactNode }) {
  const [lastRun, setRun] = useState<ResolvedRun | null>(null)
  const [lastLevelIndex, setIndex] = useState<number | null>(null)
  const [endlessRun, setEndlessRun] = useState<EndlessRunState>(IDLE_ENDLESS)

  const value = useMemo<GameContextValue>(
    () => ({
      lastRun,
      lastLevelIndex,
      setLastRun: (levelIndex, run) => {
        setIndex(levelIndex)
        setRun(run)
      },
      endlessRun,
      beginEndlessRun: () => setEndlessRun({ active: true, streak: 0, score: 0, coins: 0, finished: false }),
      recordEndlessLevel: (score, coins) =>
        setEndlessRun((s) => ({ ...s, streak: s.streak + 1, score: s.score + score, coins: s.coins + coins })),
      endEndlessRun: () => setEndlessRun((s) => ({ ...s, active: false, finished: true })),
      clearEndlessRun: () => setEndlessRun(IDLE_ENDLESS),
    }),
    [lastRun, lastLevelIndex, endlessRun],
  )

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>
}

export function useGame(): GameContextValue {
  const ctx = useContext(GameContext)
  if (!ctx) throw new Error('useGame must be used within <GameProvider>')
  return ctx
}
